import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../chrome/Icon';
import { AvatarButton } from '../chrome/Avatar';
import { MilestoneCard } from './MilestoneCard';
import { ControlBar, passesFilter, type Filter, type GroupBy } from './milestoneControls';
import { Timeline } from '../timeline/Timeline';
import { StatusSheet } from '../status/StatusSheet';
import { Leaps } from '../leaps/Leaps';
import { categories } from '../../data/milestones';
import { useBabyMilestones, useLeaps, useUpdateMilestoneStatus, useAllBabyMedia } from '../../data/queries';
import { dateChoiceForIso, isoForDateChoice, rowToLeap, rowToMilestone, serverStatus, todayIso } from '../../data/derive';
import { babyCaps } from '../../data/permissions';
import { useT } from '../../i18n';
import type { Baby, Milestone, MilestoneStatus } from '../../data/types';
import type { MediaRow } from '../../data/dto';
import { useEncryption } from '../../crypto/EncryptionProvider';
import { useCatalogOrBlob } from '../media/useMediaBlob';
import { hash, rng } from '../shared/rng';

interface Props {
  baby: Baby;
  onOpenDetail: (id: string, showTiming?: boolean) => void;
  onOpenLeap: (id: number) => void;
  /** Legacy props ignored. */
  accent?: string;
  accentInk?: string;
  onOpenProfile?: () => void;
  initialGroupBy?: GroupBy;
}

type View = 'list' | 'timeline' | 'leaps';

interface Group {
  key: string;
  label: string;
  sublabel: string;
  items: Milestone[];
}

export function MilestoneList({ baby, onOpenDetail, onOpenProfile, onOpenLeap, initialGroupBy = 'date' }: Props) {
  const caps = babyCaps(baby);
  const babyKey = baby.id ? `babysteps.ms.${baby.id}` : null;

  const [view, setViewState] = useState<View>(() => {
    if (typeof sessionStorage !== 'undefined' && babyKey) {
      const v = sessionStorage.getItem(`${babyKey}.view`);
      if (v === 'list' || v === 'timeline') return v as View;
    }
    return 'list';
  });
  const [filter, setFilterState] = useState<Filter>(() => {
    if (typeof sessionStorage !== 'undefined' && babyKey) {
      const f = sessionStorage.getItem(`${babyKey}.filter`);
      if (f === 'open' || f === 'achieved' || f === 'all') return f as Filter;
    }
    return 'open';
  });
  const [groupBy, setGroupByState] = useState<GroupBy>(() => {
    if (typeof sessionStorage !== 'undefined' && babyKey) {
      const g = sessionStorage.getItem(`${babyKey}.groupBy`);
      if (g === 'date' || g === 'category' || g === 'status') return g as GroupBy;
    }
    return initialGroupBy;
  });
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<MilestoneStatus>('soon');
  const [dateChoice, setDateChoice] = useState('today');
  const [customDate, setCustomDate] = useState(todayIso());

  const setView = (v: View) => {
    if (typeof sessionStorage !== 'undefined' && babyKey) sessionStorage.setItem(`${babyKey}.view`, v);
    setViewState(v);
  };
  const setFilter = (f: Filter) => {
    if (typeof sessionStorage !== 'undefined' && babyKey) sessionStorage.setItem(`${babyKey}.filter`, f);
    setFilterState(f);
  };
  const setGroupBy = (g: GroupBy) => {
    if (typeof sessionStorage !== 'undefined' && babyKey) sessionStorage.setItem(`${babyKey}.groupBy`, g);
    setGroupByState(g);
  };

  useEffect(() => {
    if (typeof sessionStorage === 'undefined' || !babyKey) return;
    const savedY = Number(sessionStorage.getItem(`${babyKey}.scrollY`) ?? 0);
    if (savedY > 0) {
      window.scrollTo(0, savedY);
      const timer = setTimeout(() => window.scrollTo(0, savedY), 0);
      return () => clearTimeout(timer);
    }
  }, [babyKey]);

  useEffect(() => {
    if (typeof sessionStorage === 'undefined' || !babyKey) return;
    const handleScroll = () => {
      sessionStorage.setItem(`${babyKey}.scrollY`, String(window.scrollY));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [babyKey]);

  const msQ    = useBabyMilestones(baby.id ?? null);
  const leapsQ = useLeaps();
  const updateStatus = useUpdateMilestoneStatus();
  const t = useT();

  const mediaQ = useAllBabyMedia(baby.id ?? null);
  const enc = useEncryption();

  const { media: heroMedia, isCatalog: heroIsCatalog } = useMemo<{ media: MediaRow | null; isCatalog: boolean }>(() => {
    // 1. Try to find the last user-uploaded image for the milestone category
    if (mediaQ.data) {
      const lastMilestoneImg = mediaQ.data.find(
        (m) => m.media_type === 'image' && m.entity_type === 'milestone'
      );
      if (lastMilestoneImg) {
        return { media: lastMilestoneImg, isCatalog: false };
      }
    }

    // 2. Fall back to a random catalog image from all milestones
    const catalogImages: { id: string; media_type: string; mime_type: string; is_e2e_encrypted: boolean }[] = [];
    for (const mr of msQ.data?.milestones ?? []) {
      if (mr.media) {
        for (const m of mr.media) {
          if (m.media_type === 'image') {
            catalogImages.push({
              id: m.id,
              media_type: m.media_type,
              mime_type: m.mime_type,
              is_e2e_encrypted: m.is_e2e_encrypted,
            });
          }
        }
      }
    }

    if (catalogImages.length > 0) {
      const seed = baby.id ? hash(baby.id) : 12345;
      const index = Math.floor(rng(seed)() * catalogImages.length);
      const img = catalogImages[index];
      if (img) {
        return {
          media: {
            id: img.id,
            media_type: img.media_type,
            mime_type: img.mime_type,
            is_e2e_encrypted: img.is_e2e_encrypted,
          } as MediaRow,
          isCatalog: true,
        };
      }
    }

    return { media: null, isCatalog: false };
  }, [mediaQ.data, msQ.data?.milestones, baby.id]);

  const { state: heroState, url: heroUrl } = useCatalogOrBlob(
    (heroMedia ?? {}) as MediaRow,
    heroIsCatalog,
    enc.status === 'ready',
    enc.decrypt,
    !!heroMedia,
  );

  // Convert server rows to the UI Milestone shape the screens were built on.
  const milestones: Milestone[] = useMemo(
    () => (msQ.data?.milestones ?? []).map(rowToMilestone),
    [msQ.data],
  );

  const leaps = useMemo(
    () => (leapsQ.data ?? []).map(rowToLeap),
    [leapsQ.data],
  );

  const openSheet = (id: string) => {
    const m = milestones.find((x) => x.id === id);
    if (!m) return;
    setSheetId(id);
    setDraftStatus(m.status);
    const { choice, customDate: initialDate } = dateChoiceForIso(m.achievedDate);
    setDateChoice(choice);
    setCustomDate(initialDate);
  };
  const closeSheet = () => setSheetId(null);
  const saveSheet = () => {
    if (!sheetId || !draftStatus || !baby.id) return;
    updateStatus.mutate(
      {
        babyId: baby.id,
        milestoneId: Number(sheetId),
        status: serverStatus(draftStatus),
        achieved_date: draftStatus === 'done' ? isoForDateChoice(dateChoice, customDate) : null,
      },
      { onSuccess: () => setSheetId(null) },
    );
  };

  const filtered = milestones.filter((m) => passesFilter(m.status, filter));

  let groups: Group[] = [];
  if (groupBy === 'category') {
    groups = categories
      .map((cat) => ({
        key: cat,
        label: cat,
        sublabel: `${filtered.filter((m) => m.cat === cat && m.status === 'done').length} of ${milestones.filter((m) => m.cat === cat).length}`,
        items: filtered.filter((m) => m.cat === cat),
      }))
      .filter((g) => g.items.length > 0);
  } else if (groupBy === 'leap') {
    groups = leaps
      .map((l) => ({
        key: `leap-${l.id}`,
        label: `${t('nav.leaps', 'Leap')} ${l.id} · ${l.name}`,
        sublabel: t('milestones.group.peaksWk', 'peaks wk {week}').replace('{week}', String(l.week)),
        items: filtered.filter((m) => m.leap === l.id),
      }))
      .filter((g) => g.items.length > 0);
  } else {
    groups = [
      {
        key: 'date',
        label: t('milestones.group.startsLabel', 'By start age'),
        sublabel: t('milestones.group.startsSublabel', 'earliest first'),
        items: [...filtered].sort((a, b) => a.medianWeeks - b.medianWeeks || a.startWeeks - b.startWeeks),
      },
    ];
  }

  const sheetMilestone = sheetId ? milestones.find((m) => m.id === sheetId) : null;

  return (
    <div className={[
      "relative -mx-5 nav:-mx-8 -mt-4 nav:-mt-10 bg-bg",
      view === 'list' ? 'pb-24' : 'pb-0'
    ].join(' ')}>
      {/* Hero Header Area */}
      <div className={view === 'list' ? 'relative h-60 overflow-hidden bg-tint' : 'relative h-[100px] overflow-hidden bg-tint'}>
        {heroState === 'ready' && heroUrl ? (
          <img src={heroUrl} alt="" className="size-full object-cover" />
        ) : heroState === 'loading' ? (
          <div className="size-full flex items-center justify-center bg-tint">
            <span className="size-6 animate-pulse rounded-full bg-grey-soft" />
          </div>
        ) : heroState === 'locked' ? (
          <div className="size-full flex flex-col items-center justify-center bg-tint px-6 text-center">
            <button
              onClick={() => void enc.ensureReady()}
              className="flex flex-col items-center justify-center gap-2 text-accent-ink hover:opacity-80 transition-opacity cursor-pointer"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="10" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
              <span className="text-[11px] font-bold uppercase tracking-wider">{t('common.locked')}</span>
            </button>
          </div>
        ) : (
          <div className="size-full bg-gradient-to-tr from-accent/25 via-grey/15 to-tint" />
        )}
        
        {/* 60% overlay gradient */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent 60%, var(--color-bg) 100%)' }}
        />
      </div>

      {/* Body Content */}
      <div className="relative z-2 px-5 nav:px-8 -mt-10 flex flex-col gap-4">
        {/* Page heading */}
        <header className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.13em] text-ink-3">
              {view === 'leaps' ? t('leaps.title') : t('milestones.title', 'Milestones')}
            </p>
            <h1 className="mt-1 font-serif text-[28px] nav:text-3xl leading-tight tracking-tight text-ink whitespace-pre-line">
              {view === 'leaps' ? t('leaps.gatewayTitle') : t('milestones.subtitle', 'The first 20 months')}
            </h1>
            {view === 'leaps' && (
              <p className="mt-2.5 max-w-[280px] text-[13px] leading-snug text-ink-2 text-pretty">
                {t('leaps.gatewayDesc')}
              </p>
            )}
          </div>
          <div className="nav:hidden">
            {onOpenProfile && <AvatarButton size={40} baby={baby} onOpenProfile={onOpenProfile} />}
          </div>
        </header>

      {/* List / Timeline / Leaps view switch */}
      <div className="inline-flex w-full max-w-[340px] rounded-xl bg-grey-soft p-0.5">
        {([
          { id: 'list', label: t('milestones.view.list', 'List'), icon: 'list' },
          { id: 'timeline', label: t('milestones.view.timeline', 'Timeline'), icon: 'timeline' },
          { id: 'leaps', label: t('nav.leaps', 'Leaps'), icon: 'leaps' },
        ] as const).map((v) => {
          const on = view === v.id;
          return (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={[
                'flex flex-1 items-center justify-center gap-1.5 min-h-9 rounded-[10px] px-3 text-[13px] transition-colors',
                on
                  ? 'bg-surface text-ink font-semibold shadow-[0_1px_2px_rgba(33,28,21,0.08)]'
                  : 'bg-transparent text-ink-2 font-medium',
              ].join(' ')}
            >
              <Icon name={v.icon} size={16} active={on} />
              {v.label}
            </button>
          );
        })}
      </div>

      {/* Filter + Group-by dropdowns — apply to both List and Timeline */}
      {view !== 'leaps' && (
        <ControlBar filter={filter} setFilter={setFilter} groupBy={groupBy} setGroupBy={setGroupBy} />
      )}

      {view === 'timeline' ? (
        <Timeline
          baby={baby}
          showHeader={false}
          showControls={false}
          filter={filter}
          groupBy={groupBy}
          onOpenDetail={onOpenDetail}
        />
      ) : view === 'leaps' ? (
        <Leaps
          baby={baby}
          onOpenLeap={onOpenLeap}
          showHeader={false}
        />
      ) : (
        <>
      {/* Groups */}
      <div className="flex flex-col gap-5">
        {groups.map((g) => (
          <section key={g.key}>
            <header className="mb-3 flex items-baseline justify-between gap-2 px-1">
              <h2
                className={[
                  'font-serif tracking-tight leading-snug text-ink text-pretty m-0',
                  groupBy === 'leap' ? 'text-lg' : 'text-xl',
                ].join(' ')}
              >
                {g.label}
              </h2>
              <span className="shrink-0 whitespace-nowrap text-[10.5px] font-bold uppercase tracking-wide text-ink-3">
                {g.sublabel}
              </span>
            </header>
            <div className="flex flex-col gap-2.5">
              {g.items.map((m) => (
                <MilestoneCard
                  key={m.id}
                  m={m}
                  dobIso={baby.dobIso}
                  babyAgeWeeks={baby.ageWeeks}
                  onToggle={(showTiming) => onOpenDetail(m.id, showTiming)}
                  onCycle={caps.canWrite ? () => openSheet(m.id) : undefined}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
        </>
      )}



      {sheetMilestone && (
        <StatusSheet
          open
          milestone={sheetMilestone}
          value={draftStatus}
          onChange={setDraftStatus}
          dateChoice={dateChoice}
          setDateChoice={setDateChoice}
          customDate={customDate}
          setCustomDate={setCustomDate}
          minDate={baby.dobIso}
          babyId={baby.id}
          saving={updateStatus.isPending}
          onSave={saveSheet}
          onClose={closeSheet}
        />
      )}
      </div>
    </div>
  );
}
